import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eurocar-start/artifacts/io6ga27q_eurocars-logo2.png";

export const Navbar = () => {
  const { t, lang, toggleLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/fleet", label: t("nav.fleet") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100/80 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Euro Cars" 
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to.replace("/", "") || "home"}`}
                className={`font-outfit font-medium text-sm px-4 py-2 rounded-full transition-all duration-300 ${
                  isActive(link.to)
                    ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="font-outfit text-sm font-medium px-3 py-1.5 rounded-full border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-all text-slate-600 hover:text-orange-600"
            >
              {lang === "en" ? "GR" : "EN"}
            </button>
            <Link to="/fleet">
              <Button data-testid="nav-book-btn" className="bg-slate-900 hover:bg-slate-800 text-white font-outfit font-medium rounded-full px-6 shadow-lg hover:shadow-slate-900/25 transition-all hover:-translate-y-0.5">
                {t("nav.bookNow")}
              </Button>
            </Link>
          </div>

          <button onClick={() => setOpen(!open)} className="md:hidden p-2" data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 shadow-xl" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`block font-outfit font-medium text-base px-4 py-3 rounded-xl transition-all ${
                  isActive(link.to) ? "bg-orange-500 text-white" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <button onClick={toggleLang} className="font-outfit text-sm font-medium px-4 py-2 rounded-full border border-slate-200">
                {lang === "en" ? "GR" : "EN"}
              </button>
              <Link to="/fleet" onClick={() => setOpen(false)} className="flex-1">
                <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white font-outfit rounded-full">
                  {t("nav.bookNow")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
